import { RiComputerLine, RiEyeOffLine, RiSendPlaneLine, RiUserLine } from "@remixicon/react";
import { useEffect } from "react";
import { TelegramPanel, type TelegramPanelProps } from "@/alerts-screen/telegram-panel";
import { isConfigured } from "@/alerts-screen/use-telegram-settings";
import { HIDDEN_COINS_MENU_LABEL, HiddenCoinsPanel } from "@/density/hidden-coins-panel";
import type { HiddenCoinsState } from "@/density/hidden-match";
import type { HiddenCoinOutcome } from "@/density/use-hidden-coins";
import type { SettingsReason } from "@/hooks/use-screen";
import { useTerminalSettings } from "@/terminal/terminal-settings";
import type { LiveMarket } from "@/terminal/terminal-settings-draft";
import { ACCOUNT_SECTION_LABEL, AccountSection } from "./account-section";
import { SettingsNav, type SettingsNavItem } from "./settings-nav";
import { SettingsSection } from "./settings-section";
import { TERMINAL_SECTION_LABEL, TerminalSettingsSection } from "./terminal-settings-section";

export interface SettingsScreenProps {
  /** Повод из адреса (§127 п. 7): раздел «Терминал» в поле зрения, с пояснением про группы или без. */
  reason: SettingsReason | null;
  /** Контроллер Telegram каркаса: тот же экземпляр, что кормит предупреждение «Алертов». */
  telegram: TelegramPanelProps;
  /** Скрытые монеты каркаса: тот же экземпляр, что отбирает строки на экране плотностей. */
  hiddenCoins: HiddenCoinsState;
  pendingHiddenBases: ReadonlySet<string>;
  onUnhideCoin: (base: string) => Promise<HiddenCoinOutcome>;
  onReloadHiddenCoins: () => void;
  /** Рынки живых плотностей для таблицы кодов терминала; спрашиваются при открытии страницы. */
  getLiveMarkets: () => readonly LiveMarket[];
  /** Учётная запись владельца: раздел «Аккаунт» не предлагает удаление (§130 п. 5). */
  /** `null` — ещё не известно (сведения `me` не получены): удаление не предлагается. */
  isOwner: boolean | null;
  /** Учётная запись удалена (`204`): каркас уводит на форму входа тем же путём, что «Выйти». */
  onAccountDeleted: (attempt: number) => void;
  onAccountDeleteStart: () => number;
  onAccountDeleteFailed: (attempt: number) => boolean;
  onSessionInvalid: () => void;
}

/**
 * Страница «Настройки» (§127 п. 7, design.md stage9-settings-page, Д2–Д3):
 * разделы Telegram, терминал, скрытые монеты и (stage9-account-delete) аккаунт. Монтируется только при
 * открытой странице: черновики живут в состоянии разделов и уходят вместе с
 * размонтированием, как раньше уходили при закрытии листа.
 */
export function SettingsScreen({
  reason,
  telegram,
  hiddenCoins,
  pendingHiddenBases,
  onUnhideCoin,
  onReloadHiddenCoins,
  getLiveMarkets,
  isOwner,
  onAccountDeleted,
  onAccountDeleteStart,
  onAccountDeleteFailed,
  onSessionInvalid,
}: SettingsScreenProps) {
  const { discardDraft } = telegram.controller;
  // Введённый токен не переживает страницу (web-alerts-screen): контроллер
  // живёт в каркасе дольше страницы, поэтому черновик сбрасывается при уходе.
  useEffect(() => discardDraft, [discardDraft]);

  const [terminalSettings] = useTerminalSettings();
  const telegramConfigured =
    telegram.snapshot.status === "ready" ? isConfigured(telegram.snapshot.view) : undefined;
  const navItems: readonly SettingsNavItem[] = [
    {
      id: "telegram",
      label: "Telegram",
      icon: <RiSendPlaneLine />,
      on: telegramConfigured,
    },
    {
      id: "terminal",
      label: TERMINAL_SECTION_LABEL,
      icon: <RiComputerLine />,
      on: terminalSettings.enabled,
    },
    { id: "hidden-coins", label: HIDDEN_COINS_MENU_LABEL, icon: <RiEyeOffLine /> },
    { id: "account", label: ACCOUNT_SECTION_LABEL, icon: <RiUserLine /> },
  ];

  return (
    <div className="mx-auto grid w-full grid-cols-[minmax(0,820px)] justify-center gap-8 px-4 pt-7 pb-16 lg:grid-cols-[180px_minmax(0,820px)] lg:px-6">
      <SettingsNav items={navItems} />
      <div className="flex min-w-0 flex-col gap-6">
        <TelegramPanel snapshot={telegram.snapshot} controller={telegram.controller} />
        <TerminalSettingsSection
          reason={reason}
          getLiveMarkets={getLiveMarkets}
          telegramSettled={telegram.snapshot.status !== "loading"}
        />
        <SettingsSection
          sectionId="hidden-coins"
          icon={<RiEyeOffLine className="size-4" />}
          label={HIDDEN_COINS_MENU_LABEL}
        >
          <HiddenCoinsPanel
            state={hiddenCoins}
            pendingBases={pendingHiddenBases}
            onUnhide={onUnhideCoin}
            onReload={onReloadHiddenCoins}
          />
        </SettingsSection>
        <AccountSection
          isOwner={isOwner}
          onDeleted={onAccountDeleted}
          onDeleteStart={onAccountDeleteStart}
          onDeleteFailed={onAccountDeleteFailed}
          onSessionInvalid={onSessionInvalid}
        />
      </div>
    </div>
  );
}
